import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouteMatch, Link } from "react-router-dom";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import TextField from "@material-ui/core/TextField";
import Slider from "@material-ui/core/Slider";
import Button from "@material-ui/core/Button";
import SaveIcon from "@material-ui/icons/Save";
import Snackbar from "@material-ui/core/Snackbar";
import ImageUploader from "react-images-upload";

import {
  getProbe,
  getError as getErrorSelector,
  clearError,
} from "features/probe/probeSlice";
import { RootState } from "../../../app/rootReducer";
import { updateProbeThunk } from "../probeSlice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: 20,
      paddingRight: 20,
    },
    banner: {
      border: "1px solid red",
    },
    bannerImage: {
      width: "100%",
      height: "auto",
      maxHeight: 300,
    },
    image: {
      width: "100%",
      height: "auto",
    },
    button: {
      float: "right",
      marginTop: 50,
    },
    button2: {
      float: "right",
      marginRight: 10,
      marginTop: 50,
    },
  })
);

const ProbeSettings = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const route = useRouteMatch();

  /**
   * Selectors
   */
  const selectedProbe = useSelector((state: RootState) =>
    getProbe(state, Number(route.params.id))
  );
  const errorMessage = useSelector(getErrorSelector);

  /**
   * States
   */
  const [probe, setProbe] = useState<common.Probe & { isDirty?: boolean }>();
  const [image, setImage] = useState<string>();

  /**
   * Life cycles
   */
  // Set state probe data when selector changes
  useEffect(() => {
    setProbe({...selectedProbe, isDirty: false});
  }, [selectedProbe]);

  const marks = [
    {
      value: 0,
      label: "0°C",
    },
    {
      value: 18,
      label: "18°C",
    },
    {
      value: 28,
      label: "28°C",
    },
    {
      value: 35,
      label: "35",
    },
  ];

  function valuetext(value: number) {
    return `${value}°C`;
  }

  const minTempChange = (event, value) => {
    setProbe({ ...probe, minimumTemperature: value, isDirty: true });
  };

  const maxTempChange = (event, value) => {
    setProbe({ ...probe, maximumTemperature: value, isDirty: true });
  };

  const minHumidityChange = (event, value) => {
    setProbe({ ...probe, minimumHumidity: value, isDirty: true });
  };

  const maxHumidityChange = (event, value) => {
    setProbe({ ...probe, maximumHumidity: value, isDirty: true });
  };

  const nameChange = (event) => {
    setProbe({ ...probe, name: event.target.value, isDirty: true });
  };

  const save = () => {
    dispatch(updateProbeThunk(probe));
  };

  const cancel = () => {
    setProbe({ ...selectedProbe, isDirty: false });
  };

  const onDrop = uploadedImage => {
    setProbe({...probe, image: uploadedImage[0], isDirty: true});
  };

  return probe ? (
    <React.Fragment>
      <Container className={classes.root}>
        <div>
          <h1>{probe.name} Settings</h1>
        </div>

        <div style={{ display: "flex" }}>
          <div style={{ flex: "1", maxWidth: "40%" }}>
            <img src={`data:image/jpeg;base64,${probe.image}`} alt={probe.name} className={classes.image} />

            <ImageUploader
              withIcon={true}
              onChange={onDrop}
              imgExtension={[".jpg", ".gif", ".png", ".gif"]}
              maxFileSize={5242880}
              withPreview={true}
              singleImage={true}
              buttonText={'Choose Image'}
            />
          </div>

          <div style={{ flex: "1", paddingLeft: 30 }}>
            <h3 style={{ paddingBottom: 0, marginBottom: 3 }}>Probe Name</h3>
            <TextField
              id="probeName"
              label="Standard"
              value={probe.name ?? probe?.deviceID.toString()}
              onChange={nameChange}
            />

            <h3 style={{ paddingBottom: 20 }}>Minimum Temperature</h3>
            <Slider
              defaultValue={probe.minimumTemperature}
              value={probe.minimumTemperature}
              getAriaValueText={valuetext}
              aria-labelledby="discrete-slider-always"
              step={1}
              marks={marks}
              valueLabelDisplay="on"
              max={60}
              onChange={minTempChange}
            />

            <h3 style={{ paddingBottom: 20 }}>Maximum Temperature</h3>
            <Slider
              defaultValue={probe.maximumTemperature}
              value={probe.maximumTemperature}
              getAriaValueText={valuetext}
              aria-labelledby="discrete-slider-always"
              step={1}
              marks={marks}
              valueLabelDisplay="on"
              max={60}
              onChange={maxTempChange}
            />

            <h3 style={{ paddingBottom: 20 }}>Minimum Humidity</h3>
            <Slider
              defaultValue={probe.minimumHumidity}
              value={probe.minimumHumidity}
              getAriaValueText={valuetext}
              aria-labelledby="discrete-slider-always"
              step={1}
              marks={marks}
              valueLabelDisplay="on"
              max={100}
              onChange={minHumidityChange}
            />

            <h3 style={{ paddingBottom: 20 }}>Maximum Humidity</h3>
            <Slider
              defaultValue={probe.maximumHumidity}
              value={probe.maximumHumidity}
              getAriaValueText={valuetext}
              aria-labelledby="discrete-slider-always"
              step={1}
              marks={marks}
              valueLabelDisplay="on"
              max={100}
              onChange={maxHumidityChange}
            />
          </div>
        </div>

        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.button}
          startIcon={<SaveIcon />}
          disabled={!probe.isDirty}
          onClick={save}
        >
          Save
        </Button>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          className={classes.button2}
          disabled={!probe.isDirty}
          onClick={cancel}
        >
          Cancel
        </Button>

        {errorMessage && (
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={!!errorMessage}
            onClose={() => {
              dispatch(clearError());
            }}
            message={errorMessage}
            autoHideDuration={6000}
          />
        )}
      </Container>
    </React.Fragment>
  ) : (
    <p>loading</p>
  );
};

export default ProbeSettings;
